import React, { Component } from 'react';
import Button from 'react-bootstrap/lib/Button';
import TextTruncate from 'react-text-truncate';
import TruncateWithEllipsis from '../TruncateWithEllipsis';
import { PLAYBACK_SPEEDS } from '../../localStorage';
import {
  appStoreLink,
  getEpisodeImage,
  playStoreLink,
  nextFrame,
} from '../../../helpers/serverRenderingUtils';
import StationProgressBarContainer from '../StationProgressBarContainer';
import PlayButton from '../PlayButton';
import Skip from '../svgs/Skip';
import AnchorPayLogo from '../svgs/AnchorPayLogo';
import SoundIndicator from '../SoundIndicator';
import WaitIndicator from '../WaitIndicator';
import EpisodeWaveAnimationContainer from '../EpisodeWaveAnimationContainer';
import ShareIcon from '../ShareIcon';
import Box from '../../shared/Box';
import Icon from '../../shared/Icon';
import OutboundLink from '../OutboundLink';
import UrlsToLinks from '../UrlsToLinks';
import ProgressIndicatorContainer from '../ProgressIndicatorContainer';
import {
  getSpotifyUrlFromAudio,
  getAppleMusicUrlFromAudio,
} from '../../station';
import {
  PODCAST_COLORS,
  formatDateSinceCreated,
  msToDigital,
  getRandomizedVolumeData,
} from '../../utils';
import styles from './styles.sass';

const IMG_PREFIX = 'https://d12xoj7p9moygp.cloudfront.net/images/';

const AUDIO_TYPES = {
  AD: 'ad',
  MUSIC: 'music',
  RECORDING: 'default',
  INTERLUDE: 'interlude',
};
const SkipWithHover = HoverWrappedComponent(
  Skip,
  { color: '#7F8287' },
  { color: '#292F36' }
);
const ShareIconWithHover = HoverWrappedComponent(
  ShareIcon,
  { fgColor: '#7F8287' },
  { fgColor: '#292F36' }
);
const AnchorPayLogoWithHover = HoverWrappedComponent(
  AnchorPayLogo,
  { color: '#7F8287' },
  { color: '#292F36' }
);

class EpisodeSegmentPlayer extends Component {
  constructor(props, context) {
    super(props, context);
    this.handlePreviousAudioButtonClick = this.handlePreviousAudioButtonClick.bind(
      this
    );
    this.handleNextAudioButtonClick = this.handleNextAudioButtonClick.bind(
      this
    );
    this.handleShareClick = this.handleShareClick.bind(this);
    this.handlePlayOrPauseButtonClick = this.handlePlayOrPauseButtonClick.bind(
      this
    );
    this.handleAudioIndicatorButtonClick = this.handleAudioIndicatorButtonClick.bind(
      this
    );
    this.handlePlaybackSpeedButtonClick = this.handlePlaybackSpeedButtonClick.bind(
      this
    );
  }

  // before mounting because it runs on server
  // Nice to have: Instead of randomizing this, pull from the <audio> tag itself on load
  UNSAFE_componentWillMount() {
    const { volumeData, receiveVolumeData } = this.props;
    if (!volumeData.length) {
      receiveVolumeData(getRandomizedVolumeData(8, 0.2));
    }
  }

  handlePreviousAudioButtonClick(e) {
    e.preventDefault();
    const target = 'Previous Button';
    const {
      previousAudio,
      restartAudio,
      activeIndex,
      pause,
      playOrPause,
    } = this.props;
    pause();
    activeIndex === 0 ? restartAudio(target) : previousAudio(target);
    nextFrame(() => {
      playOrPause();
    });
  }

  handleShareClick() {
    const { pause, startShare } = this.props;
    pause();
    startShare();
  }

  handleNextAudioButtonClick(e) {
    const { pause, onAudioEnded } = this.props;
    const target = 'Next Button';
    pause();
    onAudioEnded(e, target);
  }

  handlePlayOrPauseButtonClick(e) {
    e.preventDefault();
    const { isPlaying, playOrPause } = this.props;
    playOrPause(isPlaying ? 'Pause Button' : 'Play Button');
  }

  handleAudioIndicatorButtonClick(e) {
    e.preventDefault();
    const { playOrPause } = this.props;
    playOrPause('Volume Indicator');
  }

  handlePlaybackSpeedButtonClick(e) {
    e.preventDefault();
    const { setPlaybackSpeed, playbackSpeed } = this.props;
    setPlaybackSpeed(cyclePlaybackSpeed(playbackSpeed));
  }

  // 'anchored' elements are for the tablet and larger display
  // classes with the same name minus 'anchored' are for mobile-first
  render() {
    const {
      children,
      activeIndex,
      activeAudioDuration,
      audios,
      countdown,
      episode,
      episodeDuration,
      isEmbedded,
      isPlaying,
      maxCaptionLines,
      playbackSpeed,
      playedDuration,
      podcastMetadata,
      shareUrl,
      stationDuration,
      stationId,
      stationName, // or podcast name
      supportUrl,
      episodeTitle,
      volumeData,
    } = this.props;
    const { podcastAuthorName } = podcastMetadata;
    const isSingleSegmentEpisode = audios.length === 1;

    return (
      <div className={getContainerClassName({ isEmbedded, isPlaying })}>
        <div className={styles.episodeImage}>
          <img
            alt="Currently playing episode"
            src={getEpisodeImage({ episode, podcastMetadata })}
          />
        </div>

        <PlayButton
          className={styles.playButton}
          colors={PODCAST_COLORS.WHITE}
          iconColors={PODCAST_COLORS.BLACK}
          isPlaying={isPlaying}
          onClick={this.handlePlayOrPauseButtonClick}
          shadow
          size={52}
          iconSize={23}
          ariaLabel="Play or pause audio"
          style={{
            visibility: playedDuration < stationDuration ? 'visible' : 'hidden',
          }}
        />
        <div className={styles.episodeContainerInner}>
          {audios[activeIndex] && (
            <EpisodeWaveAnimationContainer
              isEmbedded={isEmbedded}
              fgColor="rgba(125,125,125,0.1)"
              className={styles.waveform}
              volumeData={volumeData}
              isPlaying={isPlaying}
            />
          )}
          {audios.map((audio, index) => {
            // only render current, previous, next for transition purposes
            if (activeIndex !== -1) {
              if (index < activeIndex - 1) return '';
              if (index > activeIndex + 1) return '';
            }
            const { key } = audio;

            const transitionStyle = getSegmentTransitionStyle(
              index,
              activeIndex
            );
            const segmentView = segmentMetadata(
              audio,
              episodeTitle,
              isSingleSegmentEpisode && episodeTitle
            );
            return (
              <div
                key={key}
                className={styles.segmentInfo}
                style={{ left: transitionStyle.left }}
              >
                <div
                  className={
                    segmentView.segmentImage ? styles.withSegmentImage : ''
                  }
                >
                  {segmentView.segmentImage && segmentView.segmentImage()}
                  {segmentView.segmentTitle && segmentView.segmentTitle()}
                </div>
                <h3>
                  <TruncateWithEllipsis lines={2}>
                    {stationName}
                    <span className={styles.byLineStation}>
                      <span> &bull; </span>
                      <EmbeddableLink
                        className={styles.stationLink}
                        to={shareUrl}
                        isEmbedded={isEmbedded}
                      >
                        By {podcastAuthorName}
                      </EmbeddableLink>
                    </span>
                    <span className={styles.byLinePublishOn}>
                      <span> &bull; </span>
                      {formatDateSinceCreated(episode.publishOn, true)}
                    </span>
                  </TruncateWithEllipsis>
                </h3>
                <div
                  className={styles.anchoredActions}
                  style={{ opacity: transitionStyle.opacity }}
                >
                  {isEmbedded && supportUrl && (
                    <OutboundLink to={supportUrl} newWindow>
                      <Button
                        className={`${styles.shareAudioButton} ${styles.supportButton}`}
                        bsSize="sm"
                      >
                        <AnchorPayLogoWithHover height={12} width={14} />
                        <span>Support</span>
                      </Button>
                    </OutboundLink>
                  )}
                  {shareAudioButton(audio, this.handleShareClick)}
                </div>
              </div>
            );
          })}
          <div
            className={`${styles.segmentInfo} ${styles.segmentLoading}`}
            style={{
              left: activeIndex !== -1 ? '100%' : 0,
              display: activeIndex !== 0 ? 'table' : 'none',
            }}
          >
            <div className={styles.segmentLoadingCentered}>
              {children ||
                (stationId !== null && audios.length === 0
                  ? emptyStationElement()
                  : playbackFinishedCountDownElement())}
            </div>
          </div>
          <div className={styles.episodeMetadata}>
            <div className={styles.episodePlaybackPosition}>
              <ProgressIndicatorContainer playedPosition={playedDuration} />
            </div>
            <div className={styles.episodeDuration}>
              {msToDigital(episodeDuration || 0)}
            </div>
          </div>
          <div className={styles.controls}>
            <div className={styles.controlsWhilePlaying}>
              <SkipWithHover
                direction="right"
                style={{
                  visibility:
                    playedDuration < stationDuration ? 'visible' : 'hidden',
                }}
                onClick={this.handleNextAudioButtonClick}
                className={styles.rightSkipButton}
              />
              <PlayButton
                className={styles.playButton}
                iconColors={PODCAST_COLORS.WHITE}
                colors={PODCAST_COLORS.GRAY}
                isPlaying={isPlaying}
                onClick={this.handlePlayOrPauseButtonClick}
                shadow
                size={35}
                iconSize={15}
                ariaLabel="Play or pause"
                style={{
                  visibility:
                    playedDuration < stationDuration ? 'visible' : 'hidden',
                }}
              />
              <SkipWithHover
                direction="left"
                style={{ visibility: audios.length ? 'visible' : 'hidden' }}
                onClick={this.handlePreviousAudioButtonClick}
                className={styles.leftSkipButton}
              />
            </div>
          </div>
          <div
            style={{
              visibility:
                playedDuration < stationDuration ? 'visible' : 'hidden',
            }}
            className={styles.playbackSpeedIndicator}
            onClick={this.handlePlaybackSpeedButtonClick}
          >
            {playbackSpeed}x
          </div>
          {isEmbedded ? (
            AnchorLogo()
          ) : (
            <SoundIndicator
              fgColor="#7F8287"
              style={{
                visibility:
                  playedDuration < stationDuration ? 'visible' : 'hidden',
              }}
              className={styles.soundIndicator}
              isPlaying={isPlaying}
              onClick={this.handleAudioIndicatorButtonClick}
              barHeight={3.5}
              barWidth={2}
            />
          )}
          <StationProgressBarContainer
            className={styles.progressBar}
            min={0}
            max={stationDuration}
            playedPosition={playedDuration}
            remainingPosition={activeAudioDuration}
            remainingColor="#A066FB"
            playingColor="#B384FB"
            playedColor="#B384FB"
            size={7}
          />
        </div>
      </div>
    );

    function emptyStationElement() {
      return (
        <div className={styles.emptyStation}>
          <p>This station is currently off-air.</p>
          {!isEmbedded && (
            <p
              style={{
                visibility: countdown !== 0 ? 'visible' : 'hidden',
              }}
            >
              {countdown !== null && countdown !== 0
                ? `Playing you a similar station in ${countdown}…`
                : 'Loading…'}
            </p>
          )}
          {isEmbedded && (
            <p>
              If you’d like to be notified the next time it updates, download
              the Anchor app for{' '}
              <OutboundLink to={appStoreLink()} newWindow>
                iOS
              </OutboundLink>{' '}
              or{' '}
              <OutboundLink to={playStoreLink()} newWindow>
                Android
              </OutboundLink>
              .
            </p>
          )}
        </div>
      );
    }

    function playbackFinishedCountDownElement() {
      return (
        <span>
          <WaitIndicator className={styles.segmentLoadingSpinner} />
          {countdown !== null && countdown !== 0
            ? `Starting in ${countdown}…`
            : 'Loading…'}
        </span>
      );
    }

    function shareAudioButton(audio = {}, handleShareClick) {
      const { audioType, audioUrl } = audio;
      const isSpotify = AUDIO_TYPES.MUSIC === audioType;
      const isApplePreview = audioUrl.indexOf('apple.com') !== -1;
      if (isSpotify) {
        return isApplePreview ? (
          <OutboundLink
            className={`${styles.shareAudioButton} ${styles.appleLink}`}
            newWindow
            to={getAppleMusicUrlFromAudio(audio)}
          >
            <Button bsSize="sm">
              <img
                alt="Apple Music"
                src={`${IMG_PREFIX}apple-logo-white-mobile.png`}
                srcSet={`${IMG_PREFIX}apple-logo-white-mobile.png 1x, ${IMG_PREFIX}apple-logo-white-mobile@2x.png 2x`}
                width={6.4}
                height={8}
              />
              <span>Listen on Apple Music</span>
            </Button>
          </OutboundLink>
        ) : (
          <OutboundLink
            className={`${styles.shareAudioButton} ${styles.spotifyLink}`}
            newWindow
            to={getSpotifyUrlFromAudio(audio)}
          >
            <Button bsSize="sm">
              <img
                alt="Spotify logo"
                src={`${IMG_PREFIX}Spotify_Logo_CMYK_White.png`}
                srcSet={`${IMG_PREFIX}Spotify_Logo_CMYK_White.png 1x, ${IMG_PREFIX}Spotify_Logo_CMYK_White@2x.png 2x`}
                width={8}
                height={8}
              />
              <span>Listen on Spotify</span>
            </Button>
          </OutboundLink>
        );
      }
      return (
        <Button
          className={styles.shareAudioButton}
          bsSize="sm"
          onClick={handleShareClick}
        >
          <ShareIconWithHover height={12} width={11} />
          <span>Share</span>
        </Button>
      );
    }

    function segmentMetadata(audio) {
      const {
        attributionUrl,
        audioType,
        caption,
        imageUrl,
        thirdPartyArtistName,
        thirdPartyTrackName,
      } = audio;
      switch (audioType) {
        case AUDIO_TYPES.MUSIC: {
          return {
            segmentTitle: () => (
              <h2>
                <TextTruncate
                  line={1}
                  truncateText="…"
                  text={thirdPartyTrackName}
                />
                <TextTruncate
                  line={1}
                  truncateText="…"
                  text={thirdPartyArtistName}
                />
              </h2>
            ),
            segmentImage: () => (
              <img
                alt="Album art"
                className={styles.albumArt}
                src={imageUrl}
                width={210}
                height={210}
              />
            ),
          };
        }
        case AUDIO_TYPES.AD: {
          const sponsorCaption = caption ? `Sponsor: ${caption}` : 'Sponsor';
          if (attributionUrl) {
            return {
              segmentTitle: () => (
                <h2>
                  <OutboundLink to={attributionUrl} newWindow>
                    {sponsorCaption}
                    <Box width={12} marginLeft={6} display="inlineBlock">
                      <Icon type="external_link" fillColor="#7f8287" />
                    </Box>
                  </OutboundLink>
                </h2>
              ),
            };
          }
          return {
            segmentTitle: () => <h2>{sponsorCaption}</h2>,
          };
        }
        default: {
          return {
            segmentTitle: () => (
              <h2>
                <UrlsToLinks lines={maxCaptionLines}>
                  {episodeTitle || 'Untitled'}
                </UrlsToLinks>
              </h2>
            ),
          };
        }
      }
    }
  }
}

export default EpisodeSegmentPlayer;

function getSegmentTransitionStyle(index, activeIndex) {
  let left = 0;
  let opacity = 1;
  if (activeIndex === -1) {
    // special case for last segment
    // left = `-${offset}px`;
    left = '-100%';
    opacity = 0;
  }
  if (index === activeIndex - 1) {
    left = '-100%';
    opacity = 0;
  }
  if (index === activeIndex + 1) {
    left = '100%';
    opacity = 0;
  }
  return {
    left,
    opacity,
  };
}

function cyclePlaybackSpeed(currentSpeed) {
  const speedKeys = Object.keys(PLAYBACK_SPEEDS);
  for (let i = 0; i < speedKeys.length; i++) {
    if (PLAYBACK_SPEEDS[speedKeys[i]] === currentSpeed) {
      return PLAYBACK_SPEEDS[speedKeys[i + 1]] || PLAYBACK_SPEEDS[speedKeys[0]];
    }
  }
  return PLAYBACK_SPEEDS.NORMAL;
}

export function AnchorLogo() {
  return (
    <OutboundLink
      className={styles.anchorLogo}
      to="https://anchor.fm"
      newWindow
    >
      <span className={styles.anchorLogoMobile}>
        <img
          alt="Anchor"
          className={styles.anchorLogoDefault}
          src={`${IMG_PREFIX}anchor-logo-embed-v3-mobile.png`}
          srcSet={`${IMG_PREFIX}anchor-logo-embed-v3-mobile.png 1x, ${IMG_PREFIX}anchor-logo-embed-v3-mobile@2x.png 2x`}
          width={15}
          height={18}
        />
        <img
          alt="Anchor"
          className={styles.anchorLogoHover}
          src={`${IMG_PREFIX}anchor-logo-embed-v3-mobile--hover.png`}
          srcSet={`${IMG_PREFIX}anchor-logo-embed-v3-mobile--hover.png 1x, ${IMG_PREFIX}anchor-logo-embed-v3-mobile--hover@2x.png 2x`}
          width={15}
          height={18}
        />
      </span>
      <span className={styles.anchorLogoDesktop}>
        <img
          alt="Anchor"
          className={styles.anchorLogoDefault}
          src={`${IMG_PREFIX}anchor-logo-embed-v3-desktop.png`}
          srcSet={`${IMG_PREFIX}anchor-logo-embed-v3-desktop.png 1x, ${IMG_PREFIX}anchor-logo-embed-v3-desktop@2x.png 2x`}
          width={71}
          height={29}
        />
        <img
          alt="Anchor"
          className={styles.anchorLogoHover}
          src={`${IMG_PREFIX}anchor-logo-embed-v3-desktop--hover.png`}
          srcSet={`${IMG_PREFIX}anchor-logo-embed-v3-desktop--hover.png 1x, ${IMG_PREFIX}anchor-logo-embed-v3-desktop--hover@2x.png 2x`}
          width={71}
          height={29}
        />
      </span>
    </OutboundLink>
  );
}

function EmbeddableLink(props) {
  const { children, isEmbedded } = props;
  const linkProps = { ...props };
  if (isEmbedded) {
    linkProps.newWindow = true;
  }
  return <OutboundLink {...linkProps}>{children}</OutboundLink>;
}

function HoverWrappedComponent(
  RenderComponent,
  defaultOverrideProps = {},
  hoverOverrideProps = {}
) {
  return props => {
    const defaultProps = { ...props, ...defaultOverrideProps };
    const hoverProps = { ...props, ...hoverOverrideProps };
    return (
      <div
        className={`${props.className || ''} ${styles.hoverWrappedComponent}`}
      >
        <RenderComponent
          {...defaultProps}
          className={styles.hoverWrappedComponentDefault}
        />
        <RenderComponent
          {...hoverProps}
          className={styles.hoverWrappedComponentHover}
        />
      </div>
    );
  };
}

function getContainerClassName({ isEmbedded, isPlaying }) {
  let className = styles.episodeContainer;
  if (isEmbedded) {
    className = `${className} ${styles.episodeContainerIsEmbedded}`;
  }
  if (isPlaying) {
    className = `${className} ${styles.episodeContainerIsPlaying}`;
  }
  return className;
}
